import { useMemo, useState, type ElementType } from 'react';
import type { Country } from 'react-phone-number-input';
import { Check } from 'lucide-react';

import { Button } from 'components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from 'components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover';
import { cn } from 'lib/utils';

type CountryOption = {
  value?: string;
  label: string;
  divider?: boolean;
};

type PhoneCountrySelectProps = {
  value?: Country;
  onChange: (value?: Country) => void;
  options: CountryOption[];
  disabled?: boolean;
  readOnly?: boolean;
  iconComponent?: ElementType<{
    country?: Country;
    label?: string;
    'aria-hidden'?: boolean;
  }>;
  onFocus?: () => void;
  onBlur?: () => void;
  className?: string;
  'aria-label'?: string;
  name?: string;
};

export function PhoneCountrySelect({
  value,
  onChange,
  options,
  disabled,
  readOnly,
  iconComponent: Icon,
  onFocus,
  onBlur,
  className,
  'aria-label': ariaLabel,
}: PhoneCountrySelectProps) {
  const [open, setOpen] = useState(false);
  const isDisabled = disabled || readOnly;

  const selectableOptions = useMemo(
    () => options.filter((option) => !option.divider && option.value !== '|'),
    [options]
  );

  const selectedOption = useMemo(
    () => selectableOptions.find((option) => option.value === value || (!option.value && !value)),
    [selectableOptions, value]
  );

  const handleSelect = (optionValue?: string) => {
    onChange(optionValue ? (optionValue as Country) : undefined);
    setOpen(false);
  };

  return (
    <div className="PhoneInputCountry">
      <Popover open={open} onOpenChange={setOpen} modal={false}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            disabled={isDisabled}
            aria-label={ariaLabel}
            className={cn(
              'PhoneInputCountrySelectButton h-auto gap-1 rounded-none px-0 py-0 hover:bg-transparent dark:hover:bg-transparent',
              className
            )}
            onFocus={onFocus}
            onBlur={onBlur}
          >
            {Icon ? (
              <Icon
                country={value}
                label={selectedOption?.label}
                aria-hidden
              />
            ) : null}
            <div className="PhoneInputCountrySelectArrow" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[min(320px,calc(100vw-2rem))] p-0"
          align="start"
          onWheel={(event) => event.stopPropagation()}
          onTouchMove={(event) => event.stopPropagation()}
        >
          <Command className="overflow-hidden">
            <CommandInput placeholder="Search country..." />
            <CommandList className="max-h-64 overscroll-contain">
              <CommandEmpty>No country found.</CommandEmpty>
              <CommandGroup className="overflow-visible">
                {selectableOptions.map((option) => {
                  const optionValue = option.value || undefined;
                  const isSelected = optionValue === value || (!optionValue && !value);

                  return (
                    <CommandItem
                      key={option.value || 'international'}
                      value={option.label}
                      onSelect={() => handleSelect(option.value)}
                      className="cursor-pointer"
                    >
                      <span className="flex-1 truncate">{option.label}</span>
                      <Check className={cn('ml-2 h-4 w-4 shrink-0', isSelected ? 'opacity-100' : 'opacity-0')} />
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}